// packages/renderer/src/plugins/datasette-client.ts
//
// Pure, DOM-free client core for talking to a Datasette instance. No eda imports
// beyond shared types and the equally pure column-filter grammar; every function
// is unit-testable in isolation and reused by both the Phase-1 importer and the
// Phase-2 live DataCollection.
//
// This TypeScript build-in mirrors the runnable, unit-tested reference in
// ../../../../eda-datasette-plugin/datasette-client.js (21 node --test cases).

import type { ColumnSpec, ColumnType, FetchOpts, FilterToken, TableInfo } from '@easydb/shared';
import { hasInnerWildcard, parseColumnFilter } from '@easydb/shared';
import { isInternalField } from '../util/internal-fields.js';
import { inferColumnType as inferType } from '../import/infer-type.js';
import { quoteBigIntegers } from '../import/big-numbers.js';

export interface DatasetteRef {
  base: string;
  db: string | null;
  table: string | null;
  query: Record<string, string>;
}

export interface PageInfo {
  rows: Array<Record<string, unknown>>;
  /** Ready-made absolute cursor URL, when the instance provides one. */
  nextUrl: string | null;
  /** Raw cursor token (`next`), which some instances send instead of `next_url`. */
  nextToken: string | null;
  hasMore: boolean;
  truncated: boolean;
}

export interface TableMeta {
  columns: ColumnSpec[];
  pks: string[];
  count: number | null;
  /**
   * True when `count` is a floor, not an exact total (Datasette's
   * `count_truncated`) — the instance gave up counting past its row cap
   * (commonly ~10k rows). A progress bar using `count` as its denominator
   * must not trust it as a target in that case.
   */
  countTruncated: boolean;
  /**
   * Whether the response carried real per-column type info (`column_details`).
   * When false the columns are just names (every type defaulted to 'string')
   * and the caller should refine types from row data — some instances answer
   * `?_extra=columns` with a bare name array and ignore `column_details`.
   */
  typed: boolean;
  raw: unknown;
}

/**
 * Readers for response JSON. Every parser below takes `unknown`, because a
 * Datasette instance is a remote server of an unknown version and its shapes are
 * exactly what these functions exist to survive. The readers keep the narrowing
 * in one place instead of spreading field access over an `any`.
 */
type JsonObject = Record<string, unknown>;

function asObject(v: unknown): JsonObject | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as JsonObject) : null;
}

/** A field of `v`, or `undefined` when `v` is not an object. */
function field(v: unknown, name: string): unknown {
  return asObject(v)?.[name];
}

function asString(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

/** The strings of an array field, non-strings dropped. */
function asStrings(v: unknown): string[] {
  return asArray(v).filter((x): x is string => typeof x === 'string');
}

/** The `rows` of a write response, non-object entries dropped. */
function rowsOf(v: unknown): Array<Record<string, unknown>> {
  return asArray(field(v, 'rows')).filter((r): r is JsonObject => asObject(r) !== null);
}

/** Datasette writes SQLite booleans as `true` or `1`. */
function truthyFlag(v: unknown): boolean {
  return v === true || v === 1;
}

/** Error carrying Datasette's uniform {ok:false,error,errors,status} shape. */
export class DatasetteError extends Error {
  status?: number;
  errors: string[];
  constructor(body: unknown, status?: number) {
    const one = asString(field(body, 'error'));
    const many = asStrings(field(body, 'errors'));
    super(one ?? (many.length > 0 ? many.join('; ') : null) ?? 'Datasette request failed');
    this.name = 'DatasetteError';
    const known = status ?? asNumber(field(body, 'status'));
    if (known !== null && known !== undefined) this.status = known;
    this.errors = many.length > 0 ? many : one ? [one] : [];
  }
}

/**
 * Parse any Datasette URL into {base, db, table, query}. Accepts instance root,
 * database, table, ".json" links and links with filters. A mount prefix is
 * absorbed into `base`; single-segment URLs are treated as a database.
 */
export function parseDatasetteUrl(input: string): DatasetteRef {
  const u = new URL(String(input).trim());
  u.pathname = u.pathname.replace(/\.(json|csv)$/i, '');
  const segments = u.pathname.split('/').filter(Boolean);
  const query: Record<string, string> = {};
  for (const [k, v] of u.searchParams) query[k] = v;

  let base: string;
  let db: string | null = null;
  let table: string | null = null;
  if (segments.length >= 2) {
    table = decodeURIComponent(segments[segments.length - 1]!);
    db = decodeURIComponent(segments[segments.length - 2]!);
    const prefix = segments.slice(0, segments.length - 2).join('/');
    base = u.origin + (prefix ? '/' + prefix : '');
  } else if (segments.length === 1) {
    db = decodeURIComponent(segments[0]!);
    base = u.origin;
  } else {
    base = u.origin;
  }
  return { base, db, table, query };
}

/** Build a table JSON URL from a ref + extra query params. */
export function buildTableUrl(ref: DatasetteRef, params: Record<string, string | number | undefined> = {}): string {
  const u = new URL(`${ref.base}/${encodeURIComponent(ref.db!)}/${encodeURIComponent(ref.table!)}.json`);
  for (const [k, v] of Object.entries({ ...ref.query, ...params })) {
    if (v != null) u.searchParams.set(k, String(v));
  }
  return u.toString();
}

/**
 * Build the next page's URL from a raw `next` TOKEN, carrying `_next` as the only
 * `_`-prefixed param.
 *
 * `buildTableUrl` merges `ref.query` — the params from the URL the user pasted —
 * so a source URL that already carried `_size` would reintroduce it here and make
 * the request two-`_`-params wide. datasette.io's Cloudflare WAF challenges any
 * `.json` request like that (see fetchTableMeta), which is the exact thing this
 * path exists to avoid. Non-underscore params (real filters like `country=AFG`)
 * are kept — dropping those would silently widen the query.
 */
export function buildTokenPageUrl(ref: DatasetteRef, token: string): string {
  const kept: Record<string, string> = {};
  for (const [k, v] of Object.entries(ref.query)) if (!k.startsWith('_')) kept[k] = v;
  return buildTableUrl({ ...ref, query: kept }, { _next: token });
}

/**
 * Re-point a server-advertised cursor URL at the origin we actually requested.
 *
 * Datasette builds `next_url` from what IT thinks its address is. Behind a
 * TLS-terminating proxy (Fly, Heroku, a reverse proxy…) it does not know the
 * request arrived over https, so it advertises **`http://…`** — e.g.
 * til.simonwillison.net returns
 * `"next_url": "http://til.simonwillison.net/tils/similarities.json?_next=…"`.
 *
 * Fetching that from an https page is blocked outright as mixed content, and the
 * browser reports it as an opaque `TypeError` ("Load failed" / "Failed to
 * fetch") — so paging died on page two and the stored resume cursor stayed
 * un-fetchable forever. Keeping the server's path + query but OUR scheme (and
 * port) fixes it, and normalising a stored cursor the same way heals an import
 * that is already stuck.
 *
 * Returns null when the cursor points at a different host: a paging cursor that
 * hops hosts is not paging, and following it would fetch somewhere else entirely.
 */
export function normaliseCursorUrl(candidate: string, requestedUrl: string): string | null {
  try {
    const req = new URL(requestedUrl);
    const next = new URL(candidate, req);
    if (next.hostname.toLowerCase() !== req.hostname.toLowerCase()) return null;
    next.protocol = req.protocol;
    next.port = req.port;
    return next.toString();
  } catch {
    return null;
  }
}

/** Ensure a URL (e.g. a next_url) carries the given params without overwriting. */
export function ensureParams(urlStr: string, params: Record<string, string | number>): string {
  const u = new URL(urlStr);
  for (const [k, v] of Object.entries(params)) {
    if (v != null && !u.searchParams.has(k)) u.searchParams.set(k, String(v));
  }
  return u.toString();
}

/**
 * Classify a Datasette JSON response: did we get everything?
 *  - hasMore:   table endpoints signal more rows via a `next_url` cursor URL
 *               and/or a raw `next` token. Some instances (e.g. datasette.io)
 *               send only the token, so we honour either.
 *  - truncated: SQL/query results hard-cap at max_returned_rows with NO cursor.
 */
export function classifyPage(json: unknown): PageInfo {
  const nextUrl = asString(field(json, 'next_url'));
  const rawNext = field(json, 'next');
  const nextToken = rawNext != null && rawNext !== false ? String(rawNext) : null;
  const rawRows = asArray(field(json, 'rows'));
  const cols: string[] | null = Array.isArray(field(json, 'columns')) ? asStrings(field(json, 'columns')) : null;
  // Modern Datasette returns row objects by default; older versions return
  // positional arrays alongside a `columns` list. Normalise arrays to objects
  // so we don't have to send `_shape=objects` (which newer versions reject).
  const rows = rawRows.map((r) => (Array.isArray(r) && cols ? Object.fromEntries(cols.map((c, i) => [c, (r as unknown[])[i]])) : (r as Record<string, unknown>)));
  return {
    rows,
    nextUrl,
    nextToken,
    hasMore: nextUrl != null || nextToken != null,
    truncated: field(json, 'truncated') === true,
  };
}

/** Map a SQLite storage type (+ column name) to an eda ColumnType. */
export function sqliteTypeToEda(sqliteType: string | undefined, name = ''): ColumnType {
  const t = String(sqliteType || '').toUpperCase();
  if (t.includes('INT')) {
    if (/^(is|has|can)_|_flag$|^enabled$|^active$/i.test(name)) return 'boolean';
    return 'number';
  }
  if (t.includes('REAL') || t.includes('FLOA') || t.includes('DOUB') || t.includes('NUM') || t.includes('DEC')) {
    return 'number';
  }
  if (t.includes('BLOB')) return 'string';
  if (/(_at|_date|^date$|^created$|^updated$|^modified$)$/i.test(name)) return 'datetime';
  return 'string';
}

/**
 * Build eda ColumnSpec[] + primary-key list from a Datasette table's metadata
 * (?_extra=columns,column_details,primary_keys). Tolerates column_details as
 * either an array of {column|name,...} or an object keyed by column name.
 */
export function mapColumns(meta: unknown): { columns: ColumnSpec[]; pks: string[] } {
  const pks: string[] = asStrings(field(meta, 'primary_keys'));
  const names: string[] = asStrings(field(meta, 'columns'));

  const details: Record<string, JsonObject> = {};
  const cd = field(meta, 'column_details');
  if (Array.isArray(cd)) {
    for (const entry of cd) {
      const d = asObject(entry);
      if (!d) continue;
      const key = asString(d['column']) ?? asString(d['name']);
      if (key) details[key] = d;
    }
  } else {
    const byName = asObject(cd);
    if (byName) {
      for (const [k, v] of Object.entries(byName)) details[k] = asObject(v) ?? {};
    }
  }
  for (const n of names) if (!(n in details)) details[n] = {};
  const order = names.length ? names : Object.keys(details);

  // `column_details` (PRAGMA table_info) may add these primary-key fields even
  // when the top-level `primary_keys` list is absent, so gather them here too.
  const columns: ColumnSpec[] = order.map((name) => {
    const d = details[name] ?? {};
    const isPk = truthyFlag(d['is_pk']) || pks.includes(name);
    const declaredType = asString(d['sqlite_type']) ?? asString(d['type']) ?? undefined;
    const spec: ColumnSpec = {
      field: name,
      label: prettifyLabel(name),
      type: sqliteTypeToEda(declaredType, name),
    };
    if (truthyFlag(d['notnull']) || isPk) spec.notnull = true;
    if (isPk) spec.unique = true;
    // Datasette's own hidden flag, plus storage plumbing (`rowid`) that has to
    // stay in the row for writes but is not user data.
    if (truthyFlag(d['hidden']) || isInternalField(name)) spec.hidden = true;
    // Carry a column's SQL default (from column_details) so an added row starts
    // with the same value the database would use.
    const dflt = d['default'];
    if (dflt != null && dflt !== '') spec.default = dflt as ColumnSpec['default'];
    return spec;
  });
  // Backfill the pk list from column_details when the response didn't carry a
  // top-level `primary_keys` array (older/newer shape differences).
  if (pks.length === 0) {
    const fromDetails = columns.filter((c) => c.unique).map((c) => c.field);
    if (fromDetails.length) pks.push(...fromDetails);
  }
  return { columns, pks };
}

/**
 * Mark a table's primary-key columns as `unique` + `notnull`.
 *
 * `mapColumns` already does this when the instance answers `?_extra=
 * column_details` (PRAGMA table_info carries `is_pk`), but plenty of instances
 * don't — datasette.io answers `column_details` with nothing at all, so its
 * columns come from `?_extra=columns` or from row inference, both of which are
 * just names. The pk list is still knowable there (`?_extra=primary_keys`), so
 * this layers the constraint back on afterwards, whatever the columns' origin.
 *
 * Idempotent, and it never clears a flag — a column the user (or a richer
 * schema response) already marked keeps its flags.
 */
export function applyPrimaryKeyFlags(columns: ColumnSpec[], pks: readonly string[]): ColumnSpec[] {
  if (pks.length === 0) return columns;
  const pkSet = new Set(pks);
  return columns.map((c) => (pkSet.has(c.field) && !(c.unique && c.notnull) ? { ...c, unique: true, notnull: true } : c));
}

function prettifyLabel(field: string): string {
  return String(field)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Infer eda columns from materialized rows. This is the fallback when a table's
 * schema endpoint (`?_extra=columns,column_details,...`) yields nothing —
 * older Datasette instances that don't support `_extra` still return row data,
 * and a table with data but no column definitions renders blank. Column order
 * is the union of keys in first-seen order; types come from the values.
 */
export function inferColumnsFromRows(rows: Array<Record<string, unknown>>): ColumnSpec[] {
  const fields: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    for (const k of Object.keys(r)) {
      if (!seen.has(k)) {
        seen.add(k);
        fields.push(k);
      }
    }
  }
  return fields.map((field) => {
    const spec: ColumnSpec = {
      field,
      label: prettifyLabel(field),
      type: inferColumnType(rows.map((r) => r[field])),
    };
    // Same rule as `mapColumns`: keep `rowid` in the data, out of sight.
    if (isInternalField(field)) spec.hidden = true;
    return spec;
  });
}

/**
 * Datasette answers in JSON, so the values are already typed — the same footing
 * as json-import, and the same inferrer. SQLite has no array type, so a list
 * arrives as a JSON string, which is exactly what an `array` column reads.
 */
function inferColumnType(values: unknown[]): ColumnType {
  return inferType(values);
}

/**
 * Tilde-encode a value for a Datasette row-PK URL segment. Any char outside
 * [A-Za-z0-9_-] becomes ~XX (uppercase hex of the UTF-8 byte). Browser-safe.
 */
export function tildeEncode(value: unknown): string {
  const bytes = new TextEncoder().encode(String(value));
  let out = '';
  for (const b of bytes) {
    const c = String.fromCharCode(b);
    if (/[A-Za-z0-9_-]/.test(c)) out += c;
    else out += '~' + b.toString(16).toUpperCase().padStart(2, '0');
  }
  return out;
}

/** Compute the URL path segment (and synthetic eda Row.id) for a row's PK. */
export function rowPk(rowData: Record<string, unknown>, pks: string[]): string | null {
  if (!pks || pks.length === 0) return null;
  return pks.map((k) => tildeEncode(rowData[k])).join(',');
}

/** No character above U+007F — the range SQLite's `LIKE` folds case over. */
const ASCII_ONLY = /^[\x20-\x7E\t\r\n]*$/;

/**
 * One token as the Datasette param that returns its rows and possibly more.
 *
 * A term carrying a star BETWEEN two pieces of text is a pattern, and `%` is
 * LIKE's spelling of the same thing — so it goes out as `__like` with the stars
 * translated and the token's own anchoring written into the ends. That is a
 * gain, not a workaround: the old code sent no param at all for this shape,
 * because it could only think in whole-string operators.
 *
 * `exact` is `__like` with nothing added, which is a whole-cell match and
 * case-insensitive — what the matcher means, and what `__exact` would get
 * wrong. A star inside an `exact` term is a literal asterisk (`hasInnerWildcard`
 * says so), and an asterisk means nothing to LIKE either, so the two agree.
 *
 * A BARE token — no anchor at all — reads as "contains" or as "is exactly"
 * depending on the `defaultSubstring` setting, which this function is not told.
 * Contains is the wider of the two, so it is the safe answer to a question we
 * cannot ask.
 */
function likeFor(col: string, t: FilterToken): [key: string, value: string] {
  if (hasInnerWildcard(t.term, t)) {
    const body = t.term.split('*').join('%');
    if (t.prefix) return [`${col}__like`, `${body}%`];
    if (t.suffix) return [`${col}__like`, `%${body}`];
    return [`${col}__like`, `%${body}%`];
  }
  if (t.exact) return [`${col}__like`, t.term];
  if (t.prefix) return [`${col}__startswith`, t.term];
  if (t.suffix) return [`${col}__endswith`, t.term];
  return [`${col}__contains`, t.term];
}

/**
 * Translate an eda table's persisted sort + column filters into Datasette query
 * params — the HTTP sibling of `filter-sql.ts`.
 *
 * **The whole contract is one word: SUPERSET.** Whatever comes back is narrowed
 * again in memory by `matchesColumnFilter`, so a param that returns too MUCH
 * costs a bigger page and nothing else. A param that returns too LITTLE drops
 * rows the user never excluded, and no later filtering can put them back — see
 * the note on `RowQuery` in `@easydb/shared/row-query.ts`. So every rule below
 * is chosen to be equal to or wider than the matcher, never narrower, and a
 * shape that cannot meet that bar sends NO param for its column.
 *
 * Which is why this reads `FilterToken` FLAGS and not the term text. The old
 * version re-parsed the term with regexes (`/^=\s*(.+)$/` and friends), but
 * `parseColumnFilter` has already eaten the operator — `=marc` arrives as
 * `{term:'marc', exact:true}` — so the `=` arm never fired and an exact filter
 * went out as `__contains`. That one was merely loose; the same blindness sent
 * `__in` for a comma list, which is exact and case-SENSITIVE where the matcher
 * means substring, and that direction loses rows.
 *
 * The operators that are safe are the LIKE-backed ones. SQLite's `LIKE` is
 * case-insensitive for ASCII, which is the matcher's rule too, so `__like`,
 * `__contains`, `__startswith` and `__endswith` all agree with it. `__exact`
 * and `__in` are `=` and `IN`, which are case-sensitive, so neither is ever
 * emitted. `%` and `_` inside a term stay live wildcards — LIKE has no escape
 * clause through a query param — but that only ever WIDENS the answer.
 *
 * Deliberately sending nothing, each because it would narrow:
 *
 *  - **A negated token.** `NOT LIKE` is unknown for a NULL cell, so SQL drops
 *    the row; the matcher passes it (a null cell contains nothing, so it
 *    satisfies "does not contain").
 *  - **A comparison** (`>=`, `<`, …). What one MEANS is type-aware here
 *    (`compare-cell.ts` normalises a zoned datetime to UTC, reads a number
 *    numerically); Datasette compares by the column's SQLite affinity, which is
 *    not the same question.
 *  - **`NULL` / `!NULL`.** The matcher's "blank" is blank-after-TRIM;
 *    `__isblank` is null-or-empty-string and misses a whitespace-only cell.
 *  - **More than one token.** Datasette takes one operator per column, and an
 *    OR of substrings has no single operator.
 *  - **The global search.** `_search` is FTS: it matches whole indexed words in
 *    FTS-enabled columns, where this app's search is a plain substring over
 *    every searchable column. Strictly narrower, in two ways at once.
 */
export function translateQuery(
  state: {
    sortColumn?: string;
    sortAsc?: boolean;
    filters?: Record<string, string>;
    search?: string;
  } = {},
): Record<string, string> {
  const params: Record<string, string> = {};
  // Sorting narrows nothing, so it needs none of the care below.
  if (state.sortColumn) params[state.sortAsc === false ? '_sort_desc' : '_sort'] = state.sortColumn;
  for (const [col, raw] of Object.entries(state.filters || {})) {
    const val = String(raw).trim();
    if (val === '') continue;
    const tokens = parseColumnFilter(val);
    // One operator per column, so one token is the only shape that fits.
    if (tokens.length !== 1) continue;
    const t = tokens[0]!;
    // Each of these would return LESS than the matcher — see the header.
    if (t.negate || t.cmp) continue;
    // The NULL test: an empty term, or `NULL` with no anchor claiming it as text.
    if (t.term.trim() === '' || (!t.prefix && !t.exact && !t.suffix && !t.contains && t.term.toUpperCase() === 'NULL')) continue;
    // SQLite's `LIKE` folds case for ASCII and ONLY for ASCII, so `%ärger%`
    // does not find "Ärger" — while the matcher's `toLowerCase()` is Unicode-
    // aware and does. That is the narrowing direction, so a term carrying any
    // non-ASCII character is not pushed down at all.
    if (!ASCII_ONLY.test(t.term)) continue;
    const [key, value] = likeFor(col, t);
    params[key] = value;
  }
  return params;
}

export interface TableRef {
  db: string;
  table: string;
  count: number | null;
  hidden: boolean;
  /** Primary-key columns (from the database listing), [] if none/unknown. */
  pks: string[];
}

/**
 * Parse `/-/databases.json` into the list of database URL segments. Returns
 * each database's `route` (which can differ from its `name` — Datasette lets a
 * database be mounted at a custom route, e.g. name `fixtures2` at route
 * `alternative-route`; URLs must use the route or they 404). Skips the built-in
 * `_memory` scratch database (never has user tables). Tolerates a bare string
 * array and a `{ databases: [...] }` wrapper.
 */
export function parseDatabaseList(json: unknown): string[] {
  const arr = Array.isArray(json) ? json : asArray(field(json, 'databases'));
  const routes: string[] = [];
  for (const entry of arr) {
    if (typeof entry === 'string') {
      routes.push(entry);
      continue;
    }
    const name = asString(field(entry, 'name'));
    if (name === null || name === '_memory') continue;
    routes.push(asString(field(entry, 'route')) || name);
  }
  return routes;
}

/**
 * Parse `/<db>.json` into a list of tables with row counts. Datasette (<1.0)
 * returns `{ tables: [{ name, count, hidden }, ...] }`; tolerate a bare array,
 * a string array, and missing count/hidden fields.
 */
export function parseTableList(json: unknown, db: string): TableRef[] {
  const arr = Array.isArray(json) ? json : asArray(field(json, 'tables'));
  const out: TableRef[] = [];
  for (const entry of arr) {
    if (typeof entry === 'string') {
      out.push({ db, table: entry, count: null, hidden: false, pks: [] });
      continue;
    }
    const name = asString(field(entry, 'name'));
    if (name === null) continue;
    out.push({
      db,
      table: name,
      count: asNumber(field(entry, 'count')),
      hidden: field(entry, 'hidden') === true,
      pks: asStrings(field(entry, 'primary_keys')),
    });
  }
  return out;
}

/** The host's URL fetch (`api.backend.fetch`), which every call here goes through. */
export type FetchFn = (url: string, opts?: FetchOpts) => Promise<Response>;

/**
 * Fetch a Datasette JSON endpoint with clear failure modes. Turns the three
 * ways a request can go wrong into a `DatasetteError` carrying a useful message
 * instead of an opaque `fetch` rejection ("Load failed" / "Failed to fetch"):
 *  - the request never completes (CORS block, DNS, offline, dead proxy);
 *  - it completes with an HTTP error status (redirect-to-non-CORS, 404, 500);
 *  - it returns Datasette's `{ ok:false, error }` envelope.
 */
async function fetchJson(fetchFn: FetchFn, url: string): Promise<unknown> {
  let res: Response;
  try {
    res = await fetchFn(url);
  } catch (err) {
    const reason = (err as Error)?.message || 'network error';
    throw new DatasetteError(
      {
        error:
          `Couldn't reach ${url} (${reason}). If this is a Datasette instance, it must be ` +
          `served with --cors for direct browser access — otherwise configure an eda sync ` +
          `server to proxy the request.`,
      },
      0,
    );
  }
  // Strict `=== false` so response doubles in tests (which omit `ok`) are fine.
  if (res && res.ok === false) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw new DatasetteError(body && typeof body === 'object' ? body : { error: `HTTP ${res.status} for ${url}` }, res.status);
  }
  // Read the body as TEXT and quote the oversized integers before parsing — the
  // same guard json-import puts in front of its own `JSON.parse`. `res.json()`
  // is already too late: an id past 2^53 has lost its last digits by the time
  // the parser hands it over, and it lands in the table silently wrong. A
  // snowflake id or a big rowid is exactly that shape.
  //
  // `text` is checked rather than assumed because `fetchFn` is `api.backend.fetch`,
  // which a plugin MAY replace (monkey-patching the api surface is contractual —
  // see plugin-api.ts), so what comes back need not be a full Response. Falling
  // back keeps such a plugin working, at the precision it would have had anyway.
  const json: unknown = typeof res.text === 'function' ? JSON.parse(quoteBigIntegers(await res.text())) : await res.json();
  if (field(json, 'ok') === false) throw new DatasetteError(json, res.status);
  return json;
}

/** List database names for an instance (`{base}/-/databases.json`). */
export async function fetchDatabaseNames(fetchFn: FetchFn, base: string): Promise<string[]> {
  return parseDatabaseList(await fetchJson(fetchFn, `${base}/-/databases.json`));
}

/** List tables (with counts) for one database (`{base}/{db}.json`). */
export async function fetchTablesForDb(fetchFn: FetchFn, base: string, db: string): Promise<TableRef[]> {
  return parseTableList(await fetchJson(fetchFn, `${base}/${encodeURIComponent(db)}.json`), db);
}

/** A SQL view in a Datasette database, with the query that defines it. */
export interface ViewRef {
  db: string;
  name: string;
  /** The `CREATE VIEW … AS SELECT …` statement, verbatim from sqlite_master. */
  sql: string;
}

/**
 * Parse the `views` key of `/<db>.json` into view names. Datasette returns a
 * string array; tolerate objects with a `name` (and a future `hidden` flag)
 * the same way `parseTableList` does.
 */
export function parseViewList(json: unknown): string[] {
  const views = (json as { views?: unknown } | null)?.views;
  const out: string[] = [];
  for (const entry of Array.isArray(views) ? views : []) {
    if (typeof entry === 'string') {
      out.push(entry);
      continue;
    }
    const o = entry as { name?: unknown; hidden?: unknown } | null;
    if (o && typeof o.name === 'string' && o.hidden !== true) out.push(o.name);
  }
  return out;
}

/**
 * Fetch each view's defining SQL from `sqlite_master`.
 *
 * There is no Datasette endpoint that exposes a view's definition — `/db.json`
 * names the views but not their queries — so this goes through the SQL
 * endpoint. That is a capability an instance can switch off, and plenty do, so
 * the failure is turned into a sentence the user can act on rather than a bare
 * 403.
 *
 * `_shape` is the ONLY `_`-prefixed param: two of them trip the Cloudflare
 * challenge described on `fetchTableMeta`, which poisons the whole session.
 */
export async function fetchViewDefinitions(fetchFn: FetchFn, base: string, db: string): Promise<ViewRef[]> {
  const sql = "select name, sql from sqlite_master where type='view' order by name";
  const url = `${base}/${encodeURIComponent(db)}.json?sql=${encodeURIComponent(sql)}&_shape=array`;
  let json: unknown;
  try {
    json = (await fetchJson(fetchFn, url)) as unknown;
  } catch (err) {
    const detail = err instanceof DatasetteError ? err.message : String(err);
    throw new DatasetteError(
      {
        error:
          `Couldn't read the view definitions from "${db}". Importing views needs the SQL ` +
          `endpoint, which this instance may have disabled (allow_sql). Its tables can still ` +
          `be imported normally.\n\n${detail}`,
      },
      err instanceof DatasetteError ? err.status : 0,
    );
  }
  const body = (json as { rows?: unknown } | null)?.rows;
  const rows: unknown[] = Array.isArray(json) ? json : Array.isArray(body) ? body : [];
  const out: ViewRef[] = [];
  for (const r of rows) {
    // `_shape=array` gives objects; a bare `rows` array gives positional pairs.
    const o = r as { name?: unknown; sql?: unknown } | null;
    const name = typeof o?.name === 'string' ? o.name : Array.isArray(r) ? r[0] : undefined;
    const stmt = typeof o?.sql === 'string' ? o.sql : Array.isArray(r) ? r[1] : undefined;
    if (typeof name === 'string' && typeof stmt === 'string' && stmt.trim()) out.push({ db, name, sql: stmt });
  }
  return out;
}

/**
 * Discover the views a URL refers to — one database, or every database of an
 * instance. A table URL narrows to that table's database, since a view lives
 * beside the tables it reads.
 *
 * The database listing is consulted FIRST, and the SQL endpoint is only touched
 * when that listing actually declares views. Most databases have none, and a
 * needless `?sql=` request on every import is both a wasted round trip and a
 * pointless 403 on the many instances that disable SQL — the listing's
 * `allow_execute_sql` flag is honoured for the same reason.
 */
export async function discoverViews(fetchFn: FetchFn, ref: DatasetteRef): Promise<ViewRef[]> {
  const dbs = ref.db ? [ref.db] : await fetchDatabaseNames(fetchFn, ref.base);
  const out: ViewRef[] = [];
  for (const db of dbs) {
    const listing = await fetchJson(fetchFn, `${ref.base}/${encodeURIComponent(db)}.json`);
    if (parseViewList(listing).length === 0) continue;
    if ((listing as { allow_execute_sql?: unknown } | null)?.allow_execute_sql === false) {
      throw new DatasetteError(
        {
          error:
            `"${db}" defines views, but this instance has SQL queries disabled ` +
            `(allow_execute_sql), and a view's definition can only be read through them. ` +
            `Its tables can still be imported normally.`,
        },
        403,
      );
    }
    out.push(...(await fetchViewDefinitions(fetchFn, ref.base, db)));
  }
  return out;
}

/**
 * Discover the importable tables a URL refers to:
 *  - table URL  (db + table) → just that table (count unknown here);
 *  - database URL (db only)   → every table in that database;
 *  - instance URL (neither)   → every table across every database.
 * Hidden tables (FTS shadow tables etc.) are excluded.
 */
export async function discoverTables(fetchFn: FetchFn, ref: DatasetteRef): Promise<TableRef[]> {
  if (ref.db && ref.table) {
    return [{ db: ref.db, table: ref.table, count: null, hidden: false, pks: [] }];
  }
  const dbs = ref.db ? [ref.db] : await fetchDatabaseNames(fetchFn, ref.base);
  const out: TableRef[] = [];
  for (const db of dbs) {
    const tables = await fetchTablesForDb(fetchFn, ref.base, db);
    for (const t of tables) if (!t.hidden) out.push(t);
  }
  return out;
}

/**
 * Fetch a table's schema, preferring rich `column_details` (real SQLite types +
 * pk / notnull / hidden / default) and falling back to bare `columns` names.
 *
 * `_extra=column_details` is the ONLY query param on the probe. Datasette.io
 * (and other Cloudflare-fronted hosts) challenge any `.json` request carrying
 * two or more `_`-prefixed params with a 302 → Turnstile page, which then flags
 * the whole browser session so even plain row fetches get bounced — the
 * "columns show but no rows" symptom. So we can neither add `_size` nor ask for
 * `columns` in the same request (a repeated `_extra` is two `_`-params), and
 * the comma form `_extra=columns,column_details` makes older instances drop
 * BOTH extras. Modern Datasette (≈1.0) answers `column_details` with the full
 * per-column schema; older ones (e.g. datasette.io on 1.0a26) don't support it
 * and return neither it nor a `columns` list — so when the first probe yields
 * no columns we make a SECOND single-`_`-param request for `columns` to recover
 * the authoritative names (important for empty tables, where row inference
 * would find nothing). Types are refined from rows when `typed` is false.
 */
export async function fetchTableMeta(fetchFn: FetchFn, ref: DatasetteRef): Promise<TableMeta> {
  const detailsUrl = buildTableUrl(ref, { _extra: 'column_details' });
  const json: unknown = await fetchJson(fetchFn, detailsUrl);
  let { columns, pks } = mapColumns(json);
  let typed = field(json, 'column_details') != null;
  let count: number | null = asNumber(field(json, 'count'));
  let countTruncated = field(json, 'count_truncated') === true;
  let raw: unknown = json;

  if (columns.length === 0) {
    // Instance doesn't support column_details — recover the names separately.
    const colsUrl = buildTableUrl(ref, { _extra: 'columns' });
    const cjson: unknown = await fetchJson(fetchFn, colsUrl);
    ({ columns, pks } = mapColumns(cjson));
    typed = field(cjson, 'column_details') != null; // still false here
    count = asNumber(field(cjson, 'count')) ?? count;
    countTruncated = field(cjson, 'count_truncated') === true || countTruncated;
    raw = cjson;
  }

  // Without `column_details` nothing so far knew which columns are the primary
  // key — `?_extra=columns` is a bare name array. Instances that drop
  // `column_details` still answer `?_extra=primary_keys` (datasette.io does),
  // so make that one extra single-`_`-param request rather than import a table
  // whose key column carries no constraint. Skipped when the schema WAS typed
  // (an empty pk list is then the truth, e.g. a rowid-only table) and
  // best-effort — a failure just leaves the pks unknown.
  if (!typed && pks.length === 0) {
    try {
      pks = await fetchPrimaryKeys(fetchFn, ref);
    } catch {
      /* no pk info available */
    }
  }
  return { columns: applyPrimaryKeyFlags(columns, pks), pks, count, countTruncated, typed, raw };
}

/**
 * Confirm a single-table URL actually points at a real table, returning its
 * TableRef. A directly-named table has no listing to fetch, so nothing else in
 * the connect/import flow verifies it exists — without this a typo'd or missing
 * table (e.g. datasette.io/legislators/officers → "Table not found") would
 * silently create an empty local table. Uses fetchTableMeta, whose single
 * `_`-param probe stays clear of datasette.io's Cloudflare challenge and throws
 * DatasetteError on a 404, which the caller surfaces to the user.
 */
export async function probeSingleTable(fetchFn: FetchFn, ref: DatasetteRef): Promise<TableRef> {
  if (!ref.db || !ref.table) {
    throw new Error('probeSingleTable: URL must name a database and a table');
  }
  const meta = await fetchTableMeta(fetchFn, ref);
  return { db: ref.db, table: ref.table, count: meta.count, hidden: false, pks: meta.pks };
}

// -- Datasette metadata (docs.datasette.io/en/stable/metadata.html) -----------

/**
 * A table's resolved Datasette metadata, normalized to eda-friendly camelCase.
 * Everything is optional — instances may ship no metadata at all. `columns` and
 * `units` are field→text maps; `sortableColumns` is null when explicitly none,
 * undefined when unrestricted (all sortable).
 */
export interface DatasetteTableMetadata {
  sort?: string;
  sortDesc?: string;
  size?: number;
  sortableColumns?: string[] | null;
  labelColumn?: string;
  hidden?: boolean;
  description?: string;
  descriptionHtml?: string;
  source?: string;
  sourceUrl?: string;
  license?: string;
  licenseUrl?: string;
  about?: string;
  aboutUrl?: string;
  columns: Record<string, string>;
  units: Record<string, string>;
}

/**
 * Extract a table's metadata from an instance `/-/metadata.json` document,
 * layering top-level source/license/about defaults under the per-table block
 * (Datasette applies attribution top-down). Pure — no I/O.
 *
 * Also used for a `/-/config.json` document, which nests its per-table blocks
 * exactly the same way — see {@link fetchTableMetadata} for why both are read.
 */
export function extractTableMetadata(metaJson: unknown, db: string | null, table: string | null): DatasetteTableMetadata {
  const root = asObject(metaJson) ?? {};
  const dbBlock = db ? (asObject(field(root['databases'], db)) ?? {}) : {};
  const tables = asObject(dbBlock['tables']) ?? {};
  // A config block Datasette redacted (it holds `allow` rules) comes back as the
  // string `"***"`, not an object — asObject rejects it and we read nothing.
  const t = (table ? asObject(tables[table]) : null) ?? (table ? asObject(tables[table.toLowerCase()]) : null) ?? {};
  // Attribution falls back to the database and then top level.
  const attr = (key: string): string | null => asString(t[key]) ?? asString(dbBlock[key]) ?? asString(root[key]);
  return readMetadataBlock(t, attr);
}

/**
 * Extract a table's metadata from a bare per-table block — the shape Datasette
 * 1.0 returns under the `metadata` key of `<db>/<table>.json?_extra=metadata`.
 * Same keys as a block inside `/-/metadata.json`, minus the instance around it,
 * so there is nothing to inherit attribution from.
 */
export function extractTableMetadataBlock(block: unknown): DatasetteTableMetadata {
  const t = asObject(block) ?? {};
  return readMetadataBlock(t, (key) => asString(t[key]));
}

/**
 * Read one table's metadata/config block. `attr` resolves the attribution keys,
 * which may inherit from enclosing scopes (see {@link extractTableMetadata}).
 */
function readMetadataBlock(t: JsonObject, attr: (key: string) => string | null): DatasetteTableMetadata {
  const out: DatasetteTableMetadata = { columns: {}, units: {} };
  const sort = asString(t['sort']);
  if (sort !== null) out.sort = sort;
  const sortDesc = asString(t['sort_desc']);
  if (sortDesc !== null) out.sortDesc = sortDesc;
  const size = asNumber(t['size']);
  if (size !== null) out.size = size;
  if (Array.isArray(t['sortable_columns'])) out.sortableColumns = asStrings(t['sortable_columns']);
  const labelColumn = asString(t['label_column']);
  if (labelColumn !== null) out.labelColumn = labelColumn;
  if (t['hidden'] === true) out.hidden = true;
  const description = asString(t['description']);
  if (description !== null) out.description = description;
  const descriptionHtml = asString(t['description_html']);
  if (descriptionHtml !== null) out.descriptionHtml = descriptionHtml;
  const src = attr('source');
  const srcUrl = attr('source_url');
  const lic = attr('license');
  const licUrl = attr('license_url');
  const abt = attr('about');
  const abtUrl = attr('about_url');
  if (src !== null) out.source = src;
  if (srcUrl !== null) out.sourceUrl = srcUrl;
  if (lic !== null) out.license = lic;
  if (licUrl !== null) out.licenseUrl = licUrl;
  if (abt !== null) out.about = abt;
  if (abtUrl !== null) out.aboutUrl = abtUrl;
  const colLabels = asObject(t['columns']);
  if (colLabels) {
    for (const [k, v] of Object.entries(colLabels)) if (typeof v === 'string') out.columns[k] = v;
  }
  const units = asObject(t['units']);
  if (units) {
    for (const [k, v] of Object.entries(units)) if (typeof v === 'string') out.units[k] = v;
  }
  return out;
}

/** Per-instance metadata cache — `/-/metadata.json` is one blob for all tables. */
const instanceMetaCache = new Map<string, Promise<unknown>>();
/** Same, for `/-/config.json` (Datasette 1.0's half of the old metadata blob). */
const instanceConfigCache = new Map<string, Promise<unknown>>();

/** Test seam: forget the cached per-instance metadata/config documents. */
export function clearInstanceMetadataCache(): void {
  instanceMetaCache.clear();
  instanceConfigCache.clear();
}

/** Fetch (and cache) an instance's `/-/metadata.json`; `{}` if unavailable. */
export async function fetchInstanceMetadata(fetchFn: FetchFn, base: string): Promise<unknown> {
  let p = instanceMetaCache.get(base);
  if (!p) {
    // No `_`-prefixed params, so this is safe against the datasette.io Cloudflare
    // WAF; a missing/blocked metadata endpoint degrades to no metadata.
    p = fetchJson(fetchFn, `${base}/-/metadata.json`).catch(() => ({}));
    instanceMetaCache.set(base, p);
  }
  return p;
}

/**
 * Fetch (and cache) an instance's `/-/config.json`; `{}` if unavailable.
 *
 * Datasette 1.0 split the old metadata blob in two: descriptive fields stayed
 * "metadata", while the behavioural ones — `sortable_columns`, `sort`,
 * `sort_desc`, `size`, `label_column` — became "config" and moved here. Absent
 * on 0.x, where `/-/metadata.json` still carries the lot.
 */
export async function fetchInstanceConfig(fetchFn: FetchFn, base: string): Promise<unknown> {
  let p = instanceConfigCache.get(base);
  if (!p) {
    p = fetchJson(fetchFn, `${base}/-/config.json`).catch(() => ({}));
    instanceConfigCache.set(base, p);
  }
  return p;
}

/**
 * Fetch a table's own metadata block via `?_extra=metadata` — Datasette 1.0's
 * only route to per-column descriptions and units, since it serves no
 * instance-wide metadata document. `{}` if unavailable.
 *
 * Single `_`-param, so it stays clear of datasette.io's WAF (see
 * {@link fetchTableMeta}) — which also means we can't add `_size=0` to suppress
 * the page of rows that rides along. One request per table, only on instances
 * that gave us no `/-/metadata.json`.
 */
export async function fetchTableMetadataExtra(fetchFn: FetchFn, ref: DatasetteRef): Promise<unknown> {
  try {
    const json = await fetchJson(fetchFn, buildTableUrl(ref, { _extra: 'metadata' }));
    return field(json, 'metadata') ?? {};
  } catch {
    return {};
  }
}

/**
 * Merge metadata layers, most-specific last. A later layer wins any scalar it
 * actually defines; `columns` / `units` merge per field rather than replacing
 * wholesale, so a description from one layer and a unit from another coexist.
 */
export function mergeTableMetadata(...layers: DatasetteTableMetadata[]): DatasetteTableMetadata {
  const out: DatasetteTableMetadata = { columns: {}, units: {} };
  for (const layer of layers) {
    const { columns, units, ...scalars } = layer;
    Object.assign(out, Object.fromEntries(Object.entries(scalars).filter(([, v]) => v !== undefined)));
    Object.assign(out.columns, columns);
    Object.assign(out.units, units);
  }
  return out;
}

/**
 * Fetch a table's resolved Datasette metadata (see {@link extractTableMetadata}).
 *
 * Reads whichever of the two era-specific layouts the instance actually serves:
 *
 *   - **0.x** — one `/-/metadata.json` document holding everything. One cached
 *     request per instance, and we stop there.
 *   - **1.0** — `/-/metadata.json` is gone (it 404s), so the same information
 *     comes from `/-/config.json` (sortable_columns, default sort, …) plus the
 *     table's own `?_extra=metadata` block (column descriptions, units,
 *     attribution).
 *
 * Every layer is best-effort; an instance serving none of them yields empty
 * metadata and every caller no-ops, exactly as before.
 */
export async function fetchTableMetadata(fetchFn: FetchFn, ref: DatasetteRef): Promise<DatasetteTableMetadata> {
  const instanceJson = await fetchInstanceMetadata(fetchFn, ref.base);
  const fromInstance = extractTableMetadata(instanceJson, ref.db, ref.table);
  // A served metadata document is authoritative and complete — don't pay for the
  // 1.0 fallbacks on an instance that has one.
  if (Object.keys(asObject(instanceJson) ?? {}).length > 0) return fromInstance;

  const [configJson, blockJson] = await Promise.all([fetchInstanceConfig(fetchFn, ref.base), ref.db && ref.table ? fetchTableMetadataExtra(fetchFn, ref) : Promise.resolve({})]);
  return mergeTableMetadata(fromInstance, extractTableMetadata(configJson, ref.db, ref.table), extractTableMetadataBlock(blockJson));
}

/** Table fields a metadata block contributes (merged into the tables.patch). */
export interface MetadataTablePatch {
  sortColumn?: string;
  sortAsc?: boolean;
  info?: TableInfo;
  labelColumn?: string;
}

/** Build a {@link TableInfo} from metadata, or undefined when nothing to show. */
function buildTableInfo(meta: DatasetteTableMetadata): TableInfo | undefined {
  const info: TableInfo = {};
  if (meta.description != null) info.description = meta.description;
  if (meta.descriptionHtml != null) info.descriptionHtml = meta.descriptionHtml;
  if (meta.source != null) info.source = meta.source;
  if (meta.sourceUrl != null) info.sourceUrl = meta.sourceUrl;
  if (meta.license != null) info.license = meta.license;
  if (meta.licenseUrl != null) info.licenseUrl = meta.licenseUrl;
  if (meta.about != null) info.about = meta.about;
  if (meta.aboutUrl != null) info.aboutUrl = meta.aboutUrl;
  return Object.keys(info).length > 0 ? info : undefined;
}

/**
 * Apply a table's Datasette metadata onto its columns + a table patch. Grows as
 * metadata features land; today it seeds the default sort (`sort`/`sort_desc`),
 * only when the named column actually exists. The grid sorts by column type, so
 * a default sort on a numeric column sorts numerically.
 */
export function applyTableMetadata(meta: DatasetteTableMetadata, columns: ColumnSpec[]): { columns: ColumnSpec[]; patch: MetadataTablePatch } {
  // A `sortable_columns` allowlist (any array, including empty) restricts which
  // columns the user may sort by; undefined ⇒ all sortable (leave unset).
  const sortAllow = meta.sortableColumns != null ? new Set(meta.sortableColumns) : null;

  // Per-column: description (header tooltip), units (header suffix), sortable
  // flag. Only set what metadata carries, so we never clobber an existing value.
  const outColumns = columns.map((c) => {
    const description = meta.columns[c.field];
    const units = meta.units[c.field];
    const sortable = sortAllow ? sortAllow.has(c.field) : undefined;
    if (description == null && units == null && sortable === undefined) return c;
    return {
      ...c,
      ...(description != null ? { description } : {}),
      ...(units != null ? { units } : {}),
      ...(sortable !== undefined ? { sortable } : {}),
    };
  });

  const fields = new Set(columns.map((c) => c.field));
  const patch: MetadataTablePatch = {};
  if (meta.sort && fields.has(meta.sort)) {
    patch.sortColumn = meta.sort;
    patch.sortAsc = true;
  } else if (meta.sortDesc && fields.has(meta.sortDesc)) {
    patch.sortColumn = meta.sortDesc;
    patch.sortAsc = false;
  }
  const info = buildTableInfo(meta);
  if (info) patch.info = info;
  if (meta.labelColumn && fields.has(meta.labelColumn)) patch.labelColumn = meta.labelColumn;
  return { columns: outColumns, patch };
}

/**
 * Upgrade column types from sampled rows when the schema came back as bare
 * names (no `column_details`, so every type defaulted to 'string'). Keeps the
 * authoritative names/labels/pk flags from the schema; only a column still
 * typed 'string' is reconsidered, and only upgraded when the rows agree on a
 * more specific type. No-op when there are no rows to learn from.
 */
export function refineColumnTypes(columns: ColumnSpec[], rows: Array<Record<string, unknown>>): ColumnSpec[] {
  if (rows.length === 0) return columns;
  const inferred = new Map(inferColumnsFromRows(rows).map((c) => [c.field, c.type]));
  return columns.map((c) => {
    if (c.type !== 'string') return c;
    const t = inferred.get(c.field);
    return t && t !== 'string' ? { ...c, type: t } : c;
  });
}

/**
 * Materialize rows by following `next_url` until the cursor is exhausted or the
 * cap is reached, returning honest completeness flags (§5.3.1 of the design).
 */
export async function fetchRows(
  fetchFn: FetchFn,
  ref: DatasetteRef,
  opts: {
    maxRows?: number;
    pageSize?: number | 'max';
    extraParams?: Record<string, string>;
    /** Called after each page with the running row total, for progress UIs. */
    onProgress?: (rowsSoFar: number) => void;
    /**
     * Resume paging from this exact page URL instead of building the first-page
     * URL. Used to continue an interrupted import from its stored cursor. In
     * resume mode a failure on the very first page does NOT throw — it returns
     * empty rows with `error` + `nextUrl` set, so the resume point is preserved.
     */
    startUrl?: string;
  } = {},
): Promise<{
  rows: Array<Record<string, unknown>>;
  truncated: boolean;
  hasMore: boolean;
  pages: number;
  /**
   * Set when paging stopped early because a page hop FAILED (e.g. rate
   * limiting / HTTP 429) after at least one page had already succeeded. The
   * rows fetched so far are still returned — the caller shows a partial result
   * instead of nothing. Undefined on a clean read. A failure on the very first
   * page throws instead (there is nothing to salvage), unless `startUrl` is set.
   */
  error?: string | undefined;
  /**
   * The page URL to resume from when paging stopped short of the end — set on an
   * interruption (the failed hop) and when the row cap was hit with a live
   * cursor. Undefined when the table was read to exhaustion. Persist it to
   * resume later via `startUrl`.
   */
  nextUrl?: string | undefined;
}> {
  const maxRows = opts.maxRows ?? 10000;
  // Fixed numeric page size for predictable cursor paging. `_size=max` is also
  // valid (Datasette clamps to max_returned_rows) but a fixed size keeps page
  // hops uniform; we follow the `next` cursor to the cap either way.
  const pageSize = opts.pageSize ?? 1000;
  // `_shape` is omitted: objects are the default on modern Datasette, and
  // classifyPage normalises positional-array rows for older instances.
  const baseParams: Record<string, string | number> = {
    _size: pageSize,
    ...(opts.extraParams || {}),
  };
  const firstUrl = buildTableUrl(ref, baseParams);
  // A stored resume cursor gets the same treatment, so an import already stuck on
  // an http:// cursor recovers on the next refresh instead of failing forever.
  let url: string | null = opts.startUrl ? (normaliseCursorUrl(opts.startUrl, firstUrl) ?? opts.startUrl) : firstUrl;
  const rows: Array<Record<string, unknown>> = [];
  let truncated = false;
  let hasMore = false;
  let pages = 0;
  let error: string | undefined;
  let resumeUrl: string | undefined;

  while (url) {
    let json: unknown;
    try {
      json = await fetchJson(fetchFn, url);
    } catch (err) {
      // A page hop failed mid-import (commonly rate limiting / HTTP 429). Don't
      // throw away the rows already fetched — return them as a partial result
      // so the user sees what loaded, and remember where to resume. A failure on
      // the very FIRST page normally propagates (nothing salvaged), EXCEPT in
      // resume mode where the resume cursor itself is the thing that failed —
      // there we return it so the import stays resumable.
      if (rows.length === 0 && !opts.startUrl) throw err;
      error = err instanceof DatasetteError && err.status ? `stopped after ${rows.length} rows: HTTP ${err.status}` : `stopped after ${rows.length} rows: ${(err as Error)?.message ?? String(err)}`;
      hasMore = true; // a cursor almost certainly remained — more is available
      resumeUrl = url; // the hop that failed — resume retries it
      break;
    }
    const info = classifyPage(json);
    rows.push(...info.rows);
    truncated = truncated || info.truncated;
    pages += 1;
    opts.onProgress?.(rows.length);

    // Follow the ready-made cursor URL if present; otherwise rebuild the table
    // URL with the `next` token (datasette.io sends only the token, no next_url).
    // The rebuilt URL carries `_next` ALONE — no `_size` — so it stays a single
    // `_`-param and doesn't trip datasette.io's Cloudflare WAF (which challenges
    // any `.json` request with two or more `_`-prefixed params; see
    // fetchTableMeta). Subsequent pages fall back to Datasette's default page
    // size, which is fine — we accumulate rows to the cap regardless.
    // A server-built next_url is re-pointed at the origin we actually requested
    // (see normaliseCursorUrl — an http:// cursor from a TLS-terminated instance
    // is blocked as mixed content). If it is unusable, fall back to rebuilding
    // from the raw token rather than giving up on paging.
    const cursorUrl: string | null = info.nextUrl != null ? normaliseCursorUrl(info.nextUrl, url) : null;
    const nextPage: string | null = cursorUrl ?? (info.nextToken != null ? buildTokenPageUrl(ref, info.nextToken) : null);

    // Keep paging while there's a cursor, we're under the cap, and the page
    // actually returned rows (the last guard prevents a pathological loop on a
    // stuck token).
    if (nextPage && rows.length < maxRows && info.rows.length > 0) {
      url = nextPage;
    } else {
      // "More available" only when a live cursor remains after a page that had
      // rows — i.e. we stopped at the cap, not because the table was exhausted.
      hasMore = nextPage != null && info.rows.length > 0;
      if (hasMore) resumeUrl = nextPage ?? undefined; // cap hit — resume from here
      url = null;
    }
  }
  return { rows, truncated, hasMore, pages, error, nextUrl: resumeUrl };
}

// -- Write API (Datasette 1.0 JSON write endpoints) --------------------------
// Verified live against latest.datasette.io/ephemeral and datasette 1.0a37:
//   insert  POST {base}/{db}/{table}/-/insert          {rows,return:true}  -> {ok,rows}
//   update  POST {base}/{db}/{table}/{pk}/-/update     {update,return:true}-> {ok,rows:[row]}
//   delete  POST {base}/{db}/{table}/{pk}/-/delete     {}                  -> {ok:true}
// Writes need Authorization: Bearer dstok_… and CORS (--cors) for direct
// browser use. `<pk>` is the tilde-encoded primary key (see rowPk/tildeEncode).

export interface WriteOpts {
  /** Datasette signed token (dstok_…). Omit for anonymous (read-only) instances. */
  token?: string | undefined;
}

function writeHeaders(token?: string): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

function tableWriteUrl(ref: DatasetteRef, action: 'insert' | 'upsert'): string {
  return `${ref.base}/${encodeURIComponent(ref.db!)}/${encodeURIComponent(ref.table!)}/-/${action}`;
}

function rowWriteUrl(ref: DatasetteRef, pkPath: string, action: 'update' | 'delete'): string {
  // pkPath is already tilde-encoded (URL-safe); do not re-encode it.
  return `${ref.base}/${encodeURIComponent(ref.db!)}/${encodeURIComponent(ref.table!)}/${pkPath}/-/${action}`;
}

/** POST a JSON write body and parse the {ok,…} / {ok:false,error} envelope. */
async function postWrite(fetchFn: FetchFn, url: string, body: unknown, token?: string): Promise<unknown> {
  let res: Response;
  try {
    res = await fetchFn(url, {
      method: 'POST',
      headers: writeHeaders(token),
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new DatasetteError({ error: `Couldn't reach ${url} (${(err as Error)?.message || 'network error'}).` }, 0);
  }
  if (res && res.ok === false) {
    let b: unknown = null;
    try {
      b = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw new DatasetteError(b && typeof b === 'object' ? b : { error: `HTTP ${res.status} for ${url}` }, res.status);
  }
  const json: unknown = await res.json();
  if (field(json, 'ok') === false) throw new DatasetteError(json, res.status);
  return json;
}

/** Insert rows; returns the server's authoritative rows (defaults, coercion). */
export async function insertRows(fetchFn: FetchFn, ref: DatasetteRef, rows: Array<Record<string, unknown>>, opts: WriteOpts = {}): Promise<Array<Record<string, unknown>>> {
  const json = await postWrite(fetchFn, tableWriteUrl(ref, 'insert'), { rows, return: true }, opts.token);
  return rowsOf(json);
}

/** Update one row by tilde-encoded PK with the changed fields; returns the row. */
export async function updateRowByPk(fetchFn: FetchFn, ref: DatasetteRef, pkPath: string, changes: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown> | null> {
  const json = await postWrite(fetchFn, rowWriteUrl(ref, pkPath, 'update'), { update: changes, return: true }, opts.token);
  const one = asObject(field(json, 'row'));
  if (one) return one;
  return rowsOf(json)[0] ?? null;
}

/** Delete one row by tilde-encoded PK. */
export async function deleteRowByPk(fetchFn: FetchFn, ref: DatasetteRef, pkPath: string, opts: WriteOpts = {}): Promise<void> {
  await postWrite(fetchFn, rowWriteUrl(ref, pkPath, 'delete'), {}, opts.token);
}

/** Upsert rows (insert or replace by PK); returns the server's rows. */
export async function upsertRows(fetchFn: FetchFn, ref: DatasetteRef, rows: Array<Record<string, unknown>>, opts: WriteOpts = {}): Promise<Array<Record<string, unknown>>> {
  const json = await postWrite(fetchFn, tableWriteUrl(ref, 'upsert'), { rows, return: true }, opts.token);
  return rowsOf(json);
}

// -- Connection / capability -------------------------------------------------

/** Fetch a table's primary-key columns (`?_extra=primary_keys`). [] if none. */
export async function fetchPrimaryKeys(fetchFn: FetchFn, ref: DatasetteRef): Promise<string[]> {
  // Single `_`-param only — see fetchTableMeta for why `_size` is not added.
  const url = buildTableUrl(ref, { _extra: 'primary_keys' });
  const json = await fetchJson(fetchFn, url);
  return asStrings(field(json, 'primary_keys'));
}

/**
 * Fetch a table's total row count via `?_extra=count` — a cheap `SELECT
 * count(*)` most instances answer even when the schema response omits `count`
 * (datasette.io does). Single `_`-param, so it's WAF-safe. `count: null` on
 * failure, so a determinate import progress bar degrades to indeterminate,
 * never errors. `truncated` mirrors Datasette's `count_truncated` — see
 * {@link TableMeta.countTruncated}.
 */
export async function fetchTableCount(fetchFn: FetchFn, ref: DatasetteRef): Promise<{ count: number | null; truncated: boolean }> {
  try {
    const json = await fetchJson(fetchFn, buildTableUrl(ref, { _extra: 'count' }));
    return {
      count: asNumber(field(json, 'count')),
      truncated: field(json, 'count_truncated') === true,
    };
  } catch {
    return { count: null, truncated: false };
  }
}

export interface ConnectionStatus {
  reachable: boolean;
  version: string | null;
  /** The authenticated actor (from `/-/actor.json`), or null if anonymous. */
  actor: Record<string, unknown> | null;
  /** A token that authenticates ⇒ treat the connection as writable. */
  writable: boolean;
  error?: string;
}

/**
 * Probe an instance: read its version and, when a token is supplied, resolve
 * the authenticated actor. `writable` is true only when a token authenticates
 * (a non-null actor) — otherwise the connection opens read-only.
 */
export async function testConnection(fetchFn: FetchFn, base: string, opts: WriteOpts = {}): Promise<ConnectionStatus> {
  const init = opts.token ? { headers: { Authorization: `Bearer ${opts.token}` } } : undefined;
  try {
    const vres = await fetchFn(`${base}/-/versions.json`, init);
    if (vres && vres.ok === false) {
      return {
        reachable: false,
        version: null,
        actor: null,
        writable: false,
        error: `HTTP ${vres.status}`,
      };
    }
    const vjson: unknown = await vres.json();
    const version = asString(field(field(vjson, 'datasette'), 'version')) ?? asString(field(vjson, 'version'));
    let actor: Record<string, unknown> | null = null;
    try {
      const ares = await fetchFn(`${base}/-/actor.json`, init);
      const ajson: unknown = await ares.json();
      actor = asObject(field(ajson, 'actor'));
    } catch {
      /* actor endpoint optional */
    }
    return { reachable: true, version, actor, writable: !!(opts.token && actor) };
  } catch (err) {
    return {
      reachable: false,
      version: null,
      actor: null,
      writable: false,
      error: (err as Error)?.message || 'unreachable',
    };
  }
}

/**
 * Wrap a fetch fn so every request carries `Authorization: Bearer <token>`
 * (merged with any existing headers). Returns the fn unchanged when there's no
 * token, so anonymous/public instances are unaffected. Used for reads too, so
 * private instances (auth required to read) work, not just writes.
 */
export function withAuthFetch(fetchFn: FetchFn, token?: string): FetchFn {
  if (!token) return fetchFn;
  return (url, opts) => {
    const prev = ((opts ?? {}) as { headers?: Record<string, string> }).headers ?? {};
    return fetchFn(url, {
      ...(opts ?? {}),
      headers: { ...prev, Authorization: `Bearer ${token}` },
    });
  };
}
